import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { useQuestions } from './hooks/useQuestions';
import {
  QuestionsFilters,
  QuestionsTable,
  QuestionDialog,
  QuestionViewDialog,
} from './components';
import { QuestionsPageProps } from './types';
import { subjectsApi } from '../../api/subjects';

export default function QuestionsPage({ isDark }: QuestionsPageProps) {
  const {
    questions,
    total,
    page,
    totalPages,
    isLoading,
    filters,
    subjects,
    topics,
    isCreateDialogOpen,
    isEditDialogOpen,
    isViewDialogOpen,
    formData,
    viewingQuestion,
    editingQuestion,
    setFilters,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setIsViewDialogOpen,
    setFormData,
    handleOpenCreate,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleView,
    handleDelete,
    handleTypeChange,
  } = useQuestions();

  const [dialogTopics, setDialogTopics] = React.useState(topics);

  React.useEffect(() => {
    setDialogTopics(topics);
  }, [topics]);

  const handleDialogSubjectChange = async (subjectId: string) => {
    if (!subjectId) {
      setDialogTopics([]);
      return;
    }
    try {
      const t = await subjectsApi.getTopics(subjectId);
      setDialogTopics(t);
    } catch {
      setDialogTopics([]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={cn(
              'text-2xl lg:text-3xl font-bold tracking-tight',
              isDark ? 'text-white' : 'text-[#0F172A]',
            )}
          >
            Questions
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Total: {total}
          </p>
        </div>
        <Button onClick={handleOpenCreate} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
          <Plus className="w-4 h-4 mr-2" /> New Question
        </Button>
      </div>

      <Card
        className={cn(
          'rounded-2xl border-0 p-4',
          isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        <QuestionsFilters
          isDark={isDark}
          filters={filters}
          setFilters={setFilters}
          subjects={subjects}
          topics={topics}
        />
      </Card>

      <Card
        className={cn(
          'rounded-2xl border-0 overflow-hidden',
          isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        <QuestionsTable
          isDark={isDark}
          questions={questions}
          subjects={subjects}
          isLoading={isLoading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-700">
            <span className="text-sm text-slate-500">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <QuestionDialog
        isDark={isDark}
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        subjects={subjects}
        topics={dialogTopics}
        onSubjectChange={handleDialogSubjectChange}
        onTypeChange={handleTypeChange}
        onSubmit={handleCreate}
      />

      <QuestionDialog
        isDark={isDark}
        isOpen={isEditDialogOpen}
        isEdit
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        subjects={subjects}
        topics={dialogTopics}
        onSubjectChange={handleDialogSubjectChange}
        onTypeChange={handleTypeChange}
        onSubmit={handleUpdate}
      />

      <QuestionViewDialog
        isDark={isDark}
        isOpen={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        question={viewingQuestion}
      />
    </div>
  );
}
